import React, { useEffect, useMemo, useState } from "react";
import Compressor from "compressorjs";
import heic2any from "heic2any";
import { uploadImage } from "../../../../Infrastructure/Services/ImageUploadService";
import { RESPONSE_CODE_INAPPROPRIATE_MEDIA } from "../../../../Infrastructure/BackendClientCodes";
import { useUserStore } from "../../../../Infrastructure/Persistence/UserState";
import {
    bytesToMB,
    isUploadedFileVideo,
    isValidImage,
    isValidVideo,
} from "../../../../Infrastructure/Helpers/ImageUploadNormalization";
import { DraggableImage, UploadedImage } from "./CommentImages.types";
import { UploadImagesInput } from "../../Post/PostForm/PostFormModal.types";
import {
    CONTENT_TEXT_ENUM,
    useInvalidMediaUploadModalState,
} from "../ServiceModals/InvalidMediaUploadModal/InvalidMediaModalState";

const IMAGES_LIMIT = 20;
const VIDEOS_LIMIT = 1;
const MAX_IMAGE_SIZE_MB = 20;
const MAX_BATCH_SIZE_MB = 250;
const MAX_VIDEO_SIZE_MB = 250;

export function useUploadPostCommentImages(input: UploadImagesInput) {
    const [images, setImages] = useState<UploadedImage[]>([]);
    const { userState } = useUserStore();
    const { setIsOpen } = useInvalidMediaUploadModalState();

    useEffect(() => {
        if (input.open) {
            setImages(input.images.filter((item) => !item.delete));
        }
    }, [input.images, input.open]);

    const handleOnAddImage = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (isUploadedFileVideo(e.target.files?.[0])) {
            setIsOpen(true, CONTENT_TEXT_ENUM.INVALID_MEDIA_TYPE);
            return;
        }

        handleAddingImage(e);
    };

    const handleOnAddVideo = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (!isUploadedFileVideo(e.target.files?.[0])) {
            setIsOpen(true, CONTENT_TEXT_ENUM.INVALID_MEDIA_TYPE);
            return;
        }

        handleAddingVideo(e);
    };

    function handleAddingImage(e: React.ChangeEvent<HTMLInputElement>) {
        if (!e.target.files) {
            return;
        }
        input.setIsUploading(true);
        const notDeletedImages = input.images.filter((image) => !image.delete);
        const isUnderImageLimit =
            notDeletedImages.length + e.target.files.length <= IMAGES_LIMIT;

        if (!isUnderImageLimit) {
            setIsOpen(true, CONTENT_TEXT_ENUM.IMAGES_NUMBER_EXCEEDED);
        }

        const imageAmount = Math.min(
            e.target.files.length,
            IMAGES_LIMIT - notDeletedImages.length,
        );

        let batchSize = 0;
        for (let i = 0; i < imageAmount; i++) {
            const file = e.target.files[i];
            batchSize += file.size;

            if (!isValidImage(file)) {
                setIsOpen(true, CONTENT_TEXT_ENUM.INVALID_MEDIA_TYPE);
                return;
            }

            if (bytesToMB(file.size) >= MAX_IMAGE_SIZE_MB) {
                setIsOpen(true, CONTENT_TEXT_ENUM.IMAGE_SIZE_EXCEEDED);
                return;
            }

            if (bytesToMB(batchSize) >= MAX_BATCH_SIZE_MB) {
                setIsOpen(true, CONTENT_TEXT_ENUM.BATCH_SIZE_EXCEEDED);
                return;
            }

            if (isValidVideo(file)) {
                setIsOpen(true, CONTENT_TEXT_ENUM.COMBINED_MEDIA_UPLOADED);
                return;
            }
        }

        saveUploadedMedia(e, imageAmount);
    }

    function handleAddingVideo(e: React.ChangeEvent<HTMLInputElement>) {
        if (!e.target.files) {
            return;
        }
        input.setIsUploading(true);

        const notDeletedVideos = input.images.filter((image) => !image.delete);
        const isUnderVideoLimit =
            notDeletedVideos.length + e.target.files.length <= VIDEOS_LIMIT;

        if (!isUnderVideoLimit) {
            setIsOpen(true, CONTENT_TEXT_ENUM.VIDEOS_NUMBER_EXCEEDED);
            return;
        }
        const videoAmount = Math.min(
            e.target.files.length,
            VIDEOS_LIMIT - notDeletedVideos.length,
        );

        let batchSize = 0;
        for (let i = 0; i < videoAmount; i++) {
            const file = e.target.files[i];
            if (!file) {
                continue;
            }

            batchSize += file.size;

            if (bytesToMB(batchSize) >= MAX_BATCH_SIZE_MB) {
                setIsOpen(true, CONTENT_TEXT_ENUM.BATCH_SIZE_EXCEEDED);
                return;
            }

            if (isValidImage(file)) {
                setIsOpen(true, CONTENT_TEXT_ENUM.COMBINED_MEDIA_UPLOADED);
                return;
            }

            if (!isValidVideo(file)) {
                setIsOpen(true, CONTENT_TEXT_ENUM.INVALID_MEDIA_TYPE);
                return;
            }

            if (bytesToMB(file.size) >= MAX_VIDEO_SIZE_MB) {
                setIsOpen(true, CONTENT_TEXT_ENUM.VIDEO_SIZE_EXCEEDED);
                return;
            }

            saveUploadedMedia(e, videoAmount);
        }
    }

    async function saveUploadedMedia(
        e: React.ChangeEvent<HTMLInputElement>,
        limit: number,
    ) {
        const selectedImages = [];
        for (let i = 0; i < limit; i++) {
            let file = e.target.files?.[i];
            if (!file) {
                continue;
            }

            if (file.type === "image/heic" || file.type === "image/heif") {
                try {
                    const convertedFile = await heic2any({
                        blob: file,
                        toType: "image/webp",
                    });
                    const blob = Array.isArray(convertedFile)
                        ? convertedFile[0]
                        : convertedFile;
                    file = new File(
                        [blob],
                        file.name.replace(/\..+$/, ".webp"),
                        { type: "image/webp" },
                    );
                } catch (error) {
                    setIsOpen(true, CONTENT_TEXT_ENUM.INVALID_MEDIA_TYPE);
                    continue;
                }
            }

            const uploadedImage: UploadedImage = {
                imageUrl: URL.createObjectURL(file),
                imageFile: file,
                delete: false,
                failed: false,
                isInappropriate: false,
                isUploading: false,
                uploaded: false,
            };
            selectedImages.push(uploadedImage);
        }
        input.setImages([
            ...input.images.filter((image) => !image.failed),
            ...selectedImages,
        ]);
        e.target.value = "";
    }

    const initUpload = (image: UploadedImage) => {
        image.isUploading = true;
        new Compressor(image.imageFile, {
            quality: 0.8,
            width: 1000,
            success: (compressed: Blob) => {
                uploadImage(compressed, input.abortController?.signal)
                    .then((res) => {
                        const data = res.data;
                        image.key = data.key;
                        image.bucket = data.bucket;
                        image.mediaType = "image";
                        image.uploaded = true;
                    })
                    .catch((err) => {
                        input.setIsUploading(false);
                        if (
                            err.response?.status ===
                            RESPONSE_CODE_INAPPROPRIATE_MEDIA
                        ) {
                            image.isInappropriate = true;
                        } else {
                            image.failed = true;
                        }
                        image.uploaded = false;
                    })
                    .finally(() => {
                        input.setIsUploading(false);
                        image.isUploading = false;
                        input.setImages([...input.images]);
                    });
            },
        });
    };

    const initUploadVideo = (image: UploadedImage) => {
        image.isUploading = true;
        uploadImage(image.imageFile, input.abortController?.signal)
            .then((res) => {
                const data = res.data;
                image.key = data.key;
                image.bucket = data.bucket;
                image.uploaded = true;
                image.mediaType = "video";
                image.videoThumbnailUrl = data.videoThumbnailUrl;
            })
            .catch((err) => {
                input.setIsUploading(false);
                if (
                    err.response?.status === RESPONSE_CODE_INAPPROPRIATE_MEDIA
                ) {
                    image.isInappropriate = true;
                } else {
                    image.failed = true;
                }
                image.uploaded = false;
            })
            .finally(() => {
                input.setIsUploading(false);
                image.isUploading = false;
                input.setImages([...input.images]);
            });
    };

    const resetUpload = (image: UploadedImage) => {
        image.failed = false;
        image.uploaded = false;
        input.setImages([...input.images]);
    };

    useEffect(() => {
        for (const image of input.images) {
            if (
                !image.id &&
                (!image.key || !image.bucket) &&
                !image.isUploading &&
                !image.failed &&
                !image.isInappropriate
            ) {
                isUploadedFileVideo(image.imageFile)
                    ? initUploadVideo(image)
                    : initUpload(image);
            }
        }
    }, [initUpload, input.images, userState, input.open]);

    const handleOnRemoveImage = (index: number) => {
        const newImages = [...input.images];
        let filteredListIndex = 0;
        for (let i = 0; i < newImages.length; i++) {
            const item = newImages[i];
            if (item.delete) {
                continue;
            }

            if (filteredListIndex === index) {
                if (item.id) {
                    item.delete = true;
                } else {
                    newImages.splice(i, 1);
                }
                break;
            }
            filteredListIndex++;
        }
        input.setImages(newImages);
    };

    const handleDragImage = (newDraggedImages: DraggableImage[]) => {
        const newImages = newDraggedImages.map((image) => image.image);
        input.setImages(newImages);
    };

    const inappropriateContentDetected = useMemo(() => {
        return images.some((image) => image.isInappropriate);
    }, [images]);

    const failedUploadsDetected = useMemo(() => {
        return images.some((image) => image.failed);
    }, [images]);

    return {
        handleOnAddImage,
        handleOnAddVideo,
        resetUpload,
        handleOnRemoveImage,
        handleDragImage,
        inappropriateContentDetected,
        failedUploadsDetected,
    };
}
